import { createRouter, createWebHistory, parseQuery, RouteRecordRaw } from 'vue-router';
import qs from 'qs';
import { ssoMenu } from '@/sso/router/routes';
import { loginMenu } from '@/login/router/routes';
import { dashboardMenu } from '@/dashboard/router/routes';
import {
  applicationMenu,
  rumMenu,
  applicationPerformanceAnalysisMenu,
} from '@/application/router/routes';
import { infrastructureMenu } from '@/infrastructure/router/routes';
import { kubernetesMenu } from '@/kubernetes/router/routes';
import { kubernetesLegacyRedirects } from '@/kubernetes/router/kubernetesLegacyRedirects';
import { databaseMenu } from '@/database/router/routes';
import { pgMenu } from '@/postgresql/router/routes';
import { oracleMenu } from '@/oracle/router/routes';
import { mysqlMenu } from '@/mysql/router/routes';
import { sqlServerMenu } from '@/sqlServer/router/routes';
import { altibaseMenu } from '@/database/router/altibase.routes';
import { tiberoMenu } from '@/database/router/tibero.routes';
import { clickhouseMenu } from '@/database/router/clickhouse.routes';
import { redshiftMenu } from '@/database/router/redshift.routes';
import { singlestoreMenu } from '@/database/router/singlestore.routes';
import { predefinedReportMenu } from '@/report/predefinedReport/router/routes';
import { aiopsMenu } from '@/aiops/router/routes';
import { analysisMenu } from '@/analysis/router/routes';
import { reportMenu } from '@/report/router/routes';
import { configMenu } from '@/config/router/routes';
import {
  requireAuth,
  setDayjsTimezone,
  skipLogin,
  ControlPageLoading,
  clearModule,
  initializeRouterGuard,
  PATH,
} from '@/common/router/navigationGuard';
import { faultAnalysisMenu } from '@/analysisTools/router/routers';
import { toolMenu } from '@/tool/router/routes';
import { alertMenu } from '@/alert/router/routes';
import { businessMenu } from '@/service/router/router';
import { loggingMenu, streamMenu } from '@/logs/router/routes';
import { helpMenu } from '@/help/router/routes';
import { addMessageEvent } from '@/common/utils/browserPopupUtils';
import PageLoading from '@/common/views/PageLoading.vue';
import type { Role } from '@/common/auth/auth.defines';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import { checkConnectionStateAtBeforeEnter } from '@/common/utils/connectionStatus.utils';
import { type ViewMode } from '@/common/stores/view-mode';
import { cloudMenu } from '@/cloud/router/routers';
import { gpuMenu } from '@/gpu/router/routes';
import { messageQueueMenu } from '@/messageQueue/router/routes';
import { homeMenu } from '@/home/router/routes';
import { kubernetesAnalysisMenu } from '@/kubernetesAnalysis/router/routes';
import { i18n } from '@/common/locale';
import { shouldShowRefreshPromptForImportError } from '@/common/utils/dynamicImportCheck.utils';
import { convertQueryToParams, isDashboardRoute } from './router.utils';
import { introRoutes } from '@/common/router/introRoutes';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { EvMessageBox } from 'evui';
import { disposeDashboardStores } from '@/dashboard/shared/utils/disposeDashboardStores';
import { useAuthStore } from '@/common/stores/auth';
import { useAiViewStore } from '@/config/stores/ai-view';

declare module 'vue-router' {
  interface RouteMeta {
    roles?: Role[];
    rolePermission?: RolePermissionKeyType;
    childRolePermission?: RolePermissionKeyType[]; // Agent, Data 등 하위 탭에도 권한 걸려있는 경우
    mode?: 'view' | 'edit';
    menuIcon?: string; // GNB panel에서 렌더링할 아이콘명
    menuIconLabel?: string; // 메뉴 아이콘 라벨
    noMenuSelect?: boolean; // true이면 클릭 시 메뉴 선택 상태로 변경하지 않음
    invisibleViewMode?: ViewMode[];
    requiresESLog?: boolean; // propertyData.useESLog가 true일 경우 접근 가능
    resourceType?: string; // 리소스 타입 분류 (예: 'batch', 'workload' 등)
    remountOnPathChange?: boolean; // 여러 라우트가 같은 컴포넌트를 공유할 때, path 변경 시 컴포넌트를 remount (setup 재실행)
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('../../login/views/Login.vue'),
    beforeEnter: skipLogin,
  },
  {
    path: '/exemone.html',
    component: () => import('../../login/views/Login.vue'),
    beforeEnter: skipLogin,
  },
  {
    path: '/manager',
    component: () => import('../../login/views/Login.vue'),
    beforeEnter: clearModule,
    props: () => ({ isManager: true }),
  },
  ...ssoMenu,
  ...loginMenu,
  ...introRoutes,
  ...homeMenu,
  ...dashboardMenu,
  ...applicationMenu,
  ...rumMenu,
  ...infrastructureMenu,
  ...gpuMenu,
  ...messageQueueMenu,
  ...cloudMenu,
  ...kubernetesMenu,
  ...kubernetesLegacyRedirects,
  ...databaseMenu,
  ...businessMenu,
  ...alertMenu,
  ...pgMenu,
  ...oracleMenu,
  ...mysqlMenu,
  ...sqlServerMenu,
  ...altibaseMenu,
  ...tiberoMenu,
  ...clickhouseMenu,
  ...redshiftMenu,
  ...singlestoreMenu,
  ...reportMenu,
  ...predefinedReportMenu,
  ...aiopsMenu,
  ...analysisMenu,
  ...configMenu,
  ...applicationPerformanceAnalysisMenu,
  ...faultAnalysisMenu,
  ...loggingMenu,
  ...streamMenu,
  ...toolMenu,
  ...helpMenu,
  ...kubernetesAnalysisMenu,
  {
    path: '/loading',
    component: PageLoading,
  },
  {
    path: '/401',
    component: BaseViewport,
    meta: {
      viewport: VIEWPORT_TYPE.ERROR,
    },
    children: [
      {
        name: 'PageAuthFail',
        path: '',
        component: () => import('../views/PageAuthFail.vue'),
      },
    ],
  },
  {
    path: '/403',
    component: BaseViewport,
    meta: {
      viewport: VIEWPORT_TYPE.ERROR,
    },
    children: [
      {
        name: 'PageForbidden',
        path: '',
        component: () => import('../views/PageForbidden.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)',
    component: BaseViewport,
    meta: {
      viewport: VIEWPORT_TYPE.ERROR,
    },
    children: [
      {
        name: 'PageNotFound',
        path: '',
        component: () => import('../views/PageNotFound.vue'),
      },
    ],
  },
  {
    path: '/popup',
    name: 'BrowserPopup',
    component: () => import('../components/templates/browserPopup/BrowserPopup.vue'),
    props: (route) => {
      const { query } = route;
      return {
        ...query,
        ...convertQueryToParams(query),
        isPopup: true,
      };
    },
    beforeEnter: [requireAuth, setDayjsTimezone],
  },
  {
    path: '/public-popup',
    name: 'PublicBrowserPopup',
    component: () => import('../components/templates/publicBrowserPopup/PublicBrowserPopup.vue'),
  },
  // [TODO] 임시로 styleGuide를 위한 라우터 추가
  {
    path: '/styleGuide',
    name: 'StyleGuide',
    component: () => import('@/styleGuide/StyleGuide.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return {
      top: 0,
    };
  },
  parseQuery: qs.parse as typeof parseQuery,
  stringifyQuery: qs.stringify,
});

router.beforeEach(initializeRouterGuard);

router.beforeResolve((to, from, next) => {
  if (!to.path.startsWith(PATH.POPUP) && !to.path.startsWith(PATH.PUBLIC_POPUP)) {
    addMessageEvent();
  }

  ControlPageLoading.inactive();

  if (to.path !== PATH.PUBLIC_POPUP) {
    checkConnectionStateAtBeforeEnter(to);
  }
  next();
});

router.onError(async (error) => {
  const shouldShowPrompt = await shouldShowRefreshPromptForImportError(error);

  if (!shouldShowPrompt) {
    return;
  }

  EvMessageBox({
    message: i18n.global.t('MESSAGE.APP_UPDATE_DETECTED'),
    closeOnClickModal: false,
    showClose: false,
    showConfirmBtn: true,
    showCancelBtn: false,
    confirmBtnText: i18n.global.t('WORD.RELOAD'),
    onClose: (type) => {
      if (type === 'ok') {
        window.location.reload();
      }
    },
  });
});

router.afterEach((to, from) => {
  // 권한 가드 리다이렉트나 중복 이동으로 beforeResolve가 건너뛰는 경우에도
  // 라우터 로딩 오버레이가 남지 않도록 최종 단계에서 정리한다.
  ControlPageLoading.inactive();

  if (isDashboardRoute(from) && !isDashboardRoute(to)) {
    disposeDashboardStores();
  }

  // 인증된 사용자의 라우트 이동 시 AI 설정을 갱신한다 (10분 throttle).
  if (useAuthStore().getToken('accessToken')) {
    useAiViewStore().fetchAiInfo().catch(console.error);
  }
});

export default router;
